import { useCallback, useMemo } from "react";
import {
	Gesture,
	GestureStateChangeEvent,
	GestureUpdateEvent,
	PanGestureChangeEventPayload,
	PanGestureHandlerEventPayload,
} from "react-native-gesture-handler";
import {
	SharedValue,
	runOnJS,
	useAnimatedReaction,
	useAnimatedStyle,
	useDerivedValue,
	useSharedValue,
	withDecay,
} from "react-native-reanimated";
import { GestureComposer } from "../types";

const useImageGalleryCarouselGesture = ({
	mainTranslateX,
	carouselItemDimensions,
	mainItemDimensions,
	isDragging,
	gestureComposer,
	processToPx,
}: {
	mainTranslateX: SharedValue<number>;
	carouselItemDimensions: {
		width: number;
		height: number;
	};
	mainItemDimensions: {
		width: number;
		height: number;
	};
	isDragging: SharedValue<boolean>;
	gestureComposer: GestureComposer | undefined;
	processToPx: (toPx: number) => { newPx: number };
}) => {
	const carouselGestureTranslateX = useSharedValue(0);
	const initialCarouselShiftTranslateX = useSharedValue(0);
	const carouselShiftTranslateX = useDerivedValue(() => {
		return mainTranslateX.value * (carouselItemDimensions.width / mainItemDimensions.width);
	}, [mainItemDimensions.width, carouselItemDimensions.width, mainTranslateX]);

	const handleNewPx = useCallback(
		(toPx: number) => {
			const { newPx } = processToPx(toPx);
			mainTranslateX.value = newPx * mainItemDimensions.width;
		},
		[processToPx, mainTranslateX, mainItemDimensions.width],
	);
	useAnimatedReaction(
		() => {
			const toPx = Math.round(
				(initialCarouselShiftTranslateX.value - carouselGestureTranslateX.value) /
					carouselItemDimensions.width,
			);
			return toPx;
		},
		(toPx, prev) => {
			if (toPx === prev) return;
			runOnJS(handleNewPx)(toPx);
		},
		[
			initialCarouselShiftTranslateX,
			carouselGestureTranslateX,
			carouselItemDimensions.width,
			handleNewPx,
		],
	);

	const didStartNewAnimation = useSharedValue(false);
	const handlePanBegin = useCallback(() => {
		"worklet";
		didStartNewAnimation.value = true;
		isDragging.value = true;
		initialCarouselShiftTranslateX.value = carouselShiftTranslateX.value;
		carouselGestureTranslateX.value = 0;
	}, [
		isDragging,
		initialCarouselShiftTranslateX,
		carouselShiftTranslateX,
		carouselGestureTranslateX,
		didStartNewAnimation,
	]);

	const handlePanChange = useCallback(
		(
			event: GestureUpdateEvent<PanGestureHandlerEventPayload & PanGestureChangeEventPayload>,
		) => {
			"worklet";
			carouselGestureTranslateX.value = event.translationX;
		},
		[carouselGestureTranslateX],
	);

	const handlePanEnd = useCallback(
		(event: GestureStateChangeEvent<PanGestureHandlerEventPayload>) => {
			"worklet";
			didStartNewAnimation.value = false;
			carouselGestureTranslateX.value = withDecay(
				{
					velocity: event.velocityX,
					deceleration: 0.99,
				},
				() => {
					carouselGestureTranslateX.value = 0;
					initialCarouselShiftTranslateX.value = carouselShiftTranslateX.value;
					if (didStartNewAnimation.value === false) {
						isDragging.value = false;
					}
				},
			);
		},
		[
			carouselShiftTranslateX,
			isDragging,
			didStartNewAnimation,
			carouselGestureTranslateX,
			initialCarouselShiftTranslateX,
		],
	);
	const gesture = useMemo(() => {
		const pan = Gesture.Pan()
			.onBegin(handlePanBegin)
			.onChange(handlePanChange)
			.onEnd(handlePanEnd);
		if (gestureComposer) {
			return gestureComposer(pan);
		}
		return pan;
	}, [handlePanBegin, handlePanChange, handlePanEnd, gestureComposer]);

	const containerStyle = useAnimatedStyle(() => {
		return {
			transform: [
				{
					translateX: isDragging.value
						? -initialCarouselShiftTranslateX.value + carouselGestureTranslateX.value
						: -carouselShiftTranslateX.value,
				},
			],
		};
	}, [
		isDragging,
		carouselShiftTranslateX,
		carouselGestureTranslateX,
		initialCarouselShiftTranslateX,
	]);
	return {
		gesture,
		containerStyle,
	};
};

export default useImageGalleryCarouselGesture;
